import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  selectAnalysisEndDate,
  selectAnalysisHasVolume,
  selectAnalysisStartDate,
  selectAnalysisStatus,
  selectMatrixVisualize,
  selectODUIMode,
  setClickedMatrixSegmentIds,
  setHoveredSegmentIds,
  useGetAnalysisMatrixQuery,
} from 'state/originDestinationSlices';
import {
  selectCurrentAnalysisId,
  selectDayType,
  selectEndTime,
  selectProject,
  selectStartTime,
} from 'state/workflowSlice';

import {
  ExtendedDayTypes,
  MIN_OD_SEGMENTS,
  ODUIMode,
  PROCESS_STATUS,
} from 'appConstants';
import { ColorGradient } from 'features/chart/colorMaps';
import StlTable from 'features/common/StlTable/StlTable';
import { ChartHeaderTitle } from 'features/workflow_nrt/ChartHeader';
import { BasePanel } from 'features/workspace/NoSegmentSelectedPanel';
import { MRT_ColumnDef } from 'material-react-table';

import ODDayTypeSelector from './ODDayTypeSelector';
import ODTimeSelector, { normalizeEndTime } from './ODTimeSelector';

import styles from './ODMatrix.module.css';

export default function CollapseODMatrix() {
  const currentAnalysisId = useAppSelector(selectCurrentAnalysisId);
  const uiMode = useAppSelector(selectODUIMode);
  const analysisStatus = useAppSelector(selectAnalysisStatus);
  const visualize = useAppSelector(selectMatrixVisualize);
  const has_volume = useAppSelector(selectAnalysisHasVolume);
  const startDate = useAppSelector(selectAnalysisStartDate);
  const endDate = useAppSelector(selectAnalysisEndDate);

  const inViewMode = uiMode === ODUIMode.View;
  const collapseOpen = Boolean(
    visualize &&
    inViewMode &&
    currentAnalysisId &&
    analysisStatus === PROCESS_STATUS.DONE,
  );

  let subtitle = '';

  if (has_volume !== undefined) {
    subtitle = has_volume ? 'Volume' : 'Trip counts';
  }
  const date = `${startDate} to ${endDate}
  `;
  if (!collapseOpen) return null;
  return (
    <BasePanel sx={{ minHeight: '400px' }}>
      <Box className={styles.header} gap={1}>
        <ChartHeaderTitle
          title="Origin-Destination Matrix"
          subtitle={subtitle}
          date={date}
        />
        <Box display="flex" gap={1}>
          <ODDayTypeSelector />
          <ODTimeSelector />
        </Box>
      </Box>
      <ODMatrix />
    </BasePanel>
  );
}

export type ODTableData = {
  Time: string;
  [ExtendedDayTypes.Friday]?: string;
  [ExtendedDayTypes.Saturday]: string;
  [ExtendedDayTypes.Sunday]?: string;
  [ExtendedDayTypes.Weekday]?: string;
  [ExtendedDayTypes.MeWeekday]?: string;
};

const stopColors = [0.0, '#000000', 0.5, '#6D829E', 1.0, '#88B8F6'];
const colorRamp = new ColorGradient(stopColors);
const HOVER_OVERLAY =
  'linear-gradient(rgba(255,255,255,0.2), rgba(255,255,255,0.2))';

function getColor(cell: any, max: number): string {
  const val = cell.getValue() ?? 0;
  const normalized = Math.log1p(val) / Math.log1p(max);
  return colorRamp.sample(normalized).toString();
}

function ODMatrix() {
  const dispatch = useAppDispatch();
  const startTime = useAppSelector(selectStartTime);
  const endTime = useAppSelector(selectEndTime);
  const dayType = useAppSelector(selectDayType);
  const project = useAppSelector(selectProject);
  const currentAnalysisId = useAppSelector(selectCurrentAnalysisId);
  const matrixVisualize = useAppSelector(selectMatrixVisualize);

  const [hovered, setHovered] = useState<{
    rowIndex: number | null;
    colIndex: number | null;
  }>({
    rowIndex: null,
    colIndex: null,
  });
  const lastHoveredSegmentKeyRef = useRef<string | null>(null);
  const { currentData, isLoading, isFetching, isError } =
    useGetAnalysisMatrixQuery(
      {
        project_slug: project,
        analysis_id: currentAnalysisId,
        startTime,
        endTime: normalizeEndTime(endTime),
        dayType,
      },
      {
        skip: !(project && endTime && startTime && dayType && matrixVisualize),
      },
    );

  const { x, y, matrix } = currentData ?? {};

  useEffect(() => {
    setHovered({ rowIndex: null, colIndex: null });
    lastHoveredSegmentKeyRef.current = null;
  }, [currentAnalysisId]);

  useEffect(() => {
    const cellHovered = hovered.rowIndex !== null && hovered.colIndex > 0;
    let segmentIds: string[] | null = null;

    if (cellHovered) {
      const xSegmentId = x?.[hovered.colIndex - 1]?.id;
      const ySegmentId = y?.[hovered.rowIndex]?.id;
      const ids = [ySegmentId, xSegmentId].filter(Boolean);
      segmentIds = ids.length >= MIN_OD_SEGMENTS ? ids : null;
    }

    const nextKey = segmentIds ? segmentIds.join('|') : null;
    if (lastHoveredSegmentKeyRef.current === nextKey) return;
    lastHoveredSegmentKeyRef.current = nextKey;
    dispatch(setHoveredSegmentIds(segmentIds));
  }, [dispatch, hovered, x, y]);

  const max = useMemo(() => {
    const values = (matrix ?? []).flat();
    return values.length ? Math.max(...values) : 0;
  }, [matrix]);

  const data = useMemo(() => {
    const formatValue = (v: number) => {
      return Math.round(v);
    };

    return (y ?? []).map((rowLabel, rowIndex) => ({
      origin: rowLabel.name,
      ...Object.fromEntries(
        (x ?? []).map((col, colIndex) => [
          col.id,
          formatValue(matrix?.[rowIndex]?.[colIndex] ?? 0),
        ]),
      ),
    }));
  }, [x, y, matrix]);

  const columns = useMemo<MRT_ColumnDef<any>[]>(() => {
    return [
      {
        accessorKey: 'origin',
        header: '',
        enableSorting: false,
        size: 80,
        minSize: 80,
        maxSize: 80,
      },
      ...(x?.map((colLabel) => ({
        accessorKey: colLabel.id,
        header: colLabel.name,
        enableSorting: false,
        size: 80,
        minSize: 80,
        maxSize: 80,
      })) ?? []),
    ];
  }, [x]);

  return (
    <div className={styles.frame}>
      <Typography className={styles.horizText}>DESTINATIONS</Typography>
      <Typography className={styles.vertText}>ORIGINS</Typography>
      <StlTable
        isLoading={isLoading || isFetching}
        classes={[styles.tableContainer]}
        columns={columns ?? []}
        data={data ?? []}
        enableColumnFilters={false}
        enableSorting={false}
        enableRowActions={false}
        muiTableBodyRowProps={{ hover: false }}
        enableColumnPinning
        enableStickyHeader
        initialState={{
          columnPinning: {
            left: ['origin'],
          },
        }}
        muiTableBodyCellProps={({ cell, row, table }) => {
          const colIndex = table
            .getVisibleLeafColumns()
            .findIndex((col) => col.id === cell.column.id);

          const isInRange =
            (data &&
              hovered.rowIndex !== null &&
              hovered.colIndex !== null &&
              row.index <= hovered.rowIndex &&
              colIndex === hovered.colIndex) ||
            (colIndex <= hovered.colIndex && row.index === hovered.rowIndex);

          return {
            onMouseEnter: () => {
              setHovered({
                rowIndex: row.index,
                colIndex,
              });
            },
            onClick: () => {
              const xSegmentId = x?.[colIndex - 1]?.id;
              const ySegmentId = y?.[row.index]?.id;
              const ids = [ySegmentId, xSegmentId].filter(Boolean);
              dispatch(
                setClickedMatrixSegmentIds(
                  ids.length === MIN_OD_SEGMENTS ? ids : null,
                ),
              );
            },
            onMouseLeave: () => {
              setHovered({
                rowIndex: null,
                colIndex: null,
              });
            },
            sx: {
              backgroundColor: colIndex !== 0 && getColor(cell, max),
              backgroundImage: isInRange ? HOVER_OVERLAY : undefined,
            },
          };
        }}
        muiTableHeadCellProps={({ column, table }) => {
          const colIndex = table
            .getVisibleLeafColumns()
            .findIndex((col) => col.id === column.id);

          const isInRange =
            data && hovered.colIndex !== null && colIndex === hovered.colIndex;

          return {
            sx: {
              backgroundImage: isInRange ? HOVER_OVERLAY : undefined,
            },
          };
        }}
        renderEmptyRowsFallback={() => {
          if (isLoading || isFetching) return null;

          if (isError)
            return <div style={{ color: 'red' }}>Failed to load data</div>;

          return <div>No data available</div>;
        }}
      />
    </div>
  );
}
